import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const PrintifyProducts = ({ layout = 'full', maxProducts = null, adminMode = false }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({ store_url: '', title: '' });

  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin' && adminMode;  // Only show Edit buttons when explicitly in admin mode

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchProducts();
  }, [layout]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Use different endpoint for sidebar vs full layout
      const endpoint = layout === 'sidebar' 
        ? `${API}/api/printify/sidebar-products`
        : `${API}/api/printify/products`;
        
      const response = await axios.get(endpoint);
      
      if (response.data.success) {
        setProducts(response.data.products);
      } else {
        setError('Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Unable to load merchandise at this time.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return (parseFloat(price) / 100).toFixed(2);
  };

  const getPriceRange = (variants) => {
    if (!variants || variants.length === 0) return 'N/A';
    
    const prices = variants.map(v => parseFloat(v.price) / 100);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  const openPrintifyProduct = (product) => {
    // Use the exact custom URL if set, otherwise use default
    const storeUrl = product.custom_store_url || `https://modernhumanbeing.printify.me/product/${product.id}`;
    window.open(storeUrl, '_blank');
  };

  const startEdit = (product) => {
    setEditingProduct(product);
    setEditForm({
      store_url: product.custom_store_url || `https://modernhumanbeing.printify.me/product/${product.id}`,
      title: product.title
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/api/printify/products/${editingProduct.id}/custom-url`, {
        custom_store_url: editForm.store_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setProducts(products.map(p => 
        p.id === editingProduct.id 
          ? { ...p, custom_store_url: editForm.store_url }
          : p
      ));
      
      setEditingProduct(null);
      alert('Store URL updated successfully!');
    } catch (error) {
      console.error('Error updating store URL:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      alert('Error updating store URL: ' + errorMsg);
    }
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setEditForm({ store_url: '', title: '' });
  };

  const ProductModal = ({ product, onClose }) => {
    if (!product) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{product.title}</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Product Images */}
              <div className="space-y-4">
                {product.images && product.images.length > 0 && (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-80 object-cover rounded-lg"
                  />
                )}
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {product.images.slice(1, 5).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${product.title} ${index + 2}`}
                        className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-75"
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="space-y-4">
                <div>
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {getPriceRange(product.variants)}
                  </div>
                  
                  {product.description && (
                    <div 
                      className="text-gray-600 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  )}
                </div>

                {/* Variants */}
                {product.variants && product.variants.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Available Options:</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {product.variants.map((variant) => (
                        <div key={variant.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm">{variant.title}</span>
                          <span className="font-semibold">${formatPrice(variant.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {product.tags && product.tags.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Categories:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.slice(0, 8).map((tag, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Buy Button */}
                <button
                  onClick={() => openPrintifyProduct(product)}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
                >
                  Shop Now on Modern Human Being Store →
                </button>

                <p className="text-xs text-gray-500 text-center">
                  You'll be redirected to our secure Printify store to complete your purchase
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">MHB Merchandise</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">MHB Merchandise</h2>
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">⚠️ {error}</div>
          <button
            onClick={fetchProducts}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const displayProducts = maxProducts ? products.slice(0, maxProducts) : products;
  const isSidebar = layout === 'sidebar';

  return (
    <>
      <div className={`bg-white rounded-2xl shadow-lg ${isSidebar ? 'p-4 text-center' : 'p-8'}`}>
        <div className="mb-4">
          <div className={`mb-2 ${isSidebar ? 'text-center' : 'flex items-center justify-between'}`}>
            <h2 className={`font-bold text-gray-800 ${isSidebar ? 'text-lg' : 'text-2xl'}`}>
              Conscious Commerce
            </h2>
            {!isSidebar && (
              <div className="text-sm text-gray-600">
                {products.length} item{products.length !== 1 ? 's' : ''} available
              </div>
            )}
          </div>
          {isSidebar && (
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              With our Triple Win model, every purchase helps us grow, you get fun merch, and society benefits—with 33% of proceeds going straight to a designated charity.
            </p>
          )}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">No products available at the moment</div>
            <button
              onClick={fetchProducts}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>
        ) : (
          <div className={`grid gap-4 ${
            isSidebar 
              ? 'grid-cols-1' 
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {displayProducts.map((product) => (
              <div
                key={product.id}
                className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200"
              >
                {/* Product Image - clickable for details */}
                {product.images && product.images.length > 0 && (
                  <div 
                    className={`overflow-hidden cursor-pointer ${isSidebar ? 'aspect-[4/3]' : 'aspect-square'}`}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <img
                      src={product.images[0]}
                      alt={product.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}

                {/* Product Info - Only clickable if not in edit mode */}
                <div 
                  className={`${isSidebar ? 'p-3' : 'p-4'} ${editingProduct?.id === product.id ? '' : 'cursor-pointer'}`}
                  onClick={editingProduct?.id === product.id ? undefined : () => setSelectedProduct(product)}
                >
                  <h3 className={`font-semibold text-gray-800 mb-2 line-clamp-2 ${
                    isSidebar ? 'text-sm' : 'text-base'
                  }`}>
                    {product.title}
                  </h3>
                  
                  <div className={`font-bold text-green-600 mb-2 ${
                    isSidebar ? 'text-lg' : 'text-xl'
                  }`}>
                    {getPriceRange(product.variants)}
                  </div>

                  {/* Tags - Only show in full layout */}
                  {!isSidebar && product.tags && product.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                      {product.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          +{product.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {editingProduct && editingProduct.id === product.id ? (
                    // Edit mode - show URL input and save/cancel buttons
                    <div className="space-y-2">
                      <input
                        type="url"
                        value={editForm.store_url}
                        onChange={(e) => setEditForm({...editForm, store_url: e.target.value})}
                        placeholder="Store URL"
                        className="w-full px-2 py-1 border rounded text-xs"
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={saveEdit}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs py-1"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex-1 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs py-1"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Normal mode - show Shop button and Edit button (if admin)
                    <div className="flex gap-1">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openPrintifyProduct(product);
                        }}
                        className={`flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium ${
                          isSidebar ? 'py-1.5 px-3 text-xs' : 'py-2 px-4 text-sm'
                        }`}
                      >
                        {isSidebar ? 'Shop' : 'View Details'}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            startEdit(product);
                          }}
                          className={`bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors ${
                            isSidebar ? 'py-1.5 px-2 text-xs' : 'py-2 px-3 text-sm'
                          }`}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom section - different for sidebar vs full */}
        {isSidebar ? (
          <div className="mt-4">
            {products.length > maxProducts && (
              <div className="text-center">
                <a 
                  href="/store" 
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View All {products.length} Products →
                </a>
              </div>
            )}
            <div className="mt-3 p-3 bg-green-50 rounded-lg">
              <p className="text-green-700 text-xs text-center">
                🌱 Triple Win™ - You, Community, Planet
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-8 p-4 bg-green-50 rounded-xl">
            <h3 className="font-semibold text-green-800 mb-3">🌱 Triple Win™ Commerce</h3>
            <p className="text-green-700 text-sm leading-relaxed">
              With our Triple Win model, every purchase helps us grow, you get fun merch, and society benefits—with 33% of proceeds going straight to a designated charity.
            </p>
          </div>
        )}
      </div>

      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
      />
    </>
  );
};

export default PrintifyProducts;